import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LeadForm } from '../components/LeadForm';
import { useLanguage } from '../context/LanguageContext';
import { 
  Clock, 
  Percent, 
  Building2, 
  FileCheck, 
  ArrowRight,
  Phone,
  CheckCircle,
  TrendingUp,
  Home,
  Building,
  RefreshCw,
  Scale
} from 'lucide-react';

export default function HomePage() {
  const { t } = useLanguage();

  const valueProps = [
    {
      icon: Clock,
      title: t('valueProps.dayClosing'),
      description: t('valueProps.dayClosingDesc'),
      highlight: '5-7',
    },
    {
      icon: Percent,
      title: t('valueProps.ltv'),
      description: t('valueProps.ltvDesc'),
      highlight: '75%',
    },
    {
      icon: Building2,
      title: t('valueProps.propertyTypes'),
      description: t('valueProps.propertyTypesDesc'),
      highlight: t('valueProps.propertyTypes'),
    },
    {
      icon: FileCheck,
      title: t('valueProps.assetBased'),
      description: t('valueProps.assetBasedDesc'),
      highlight: t('valueProps.assetBased'),
    },
  ];

  const loanPrograms = [
    {
      icon: TrendingUp,
      title: t('loanPrograms.fixFlipTitle'),
      description: t('loanPrograms.fixFlipDesc'),
      features: t('loanFeatures.fixFlip'),
    },
    {
      icon: ArrowRight,
      title: t('loanPrograms.bridgeTitle'),
      description: t('loanPrograms.bridgeDesc'),
      features: t('loanFeatures.bridge'),
    },
    {
      icon: Home,
      title: t('loanPrograms.rentalTitle'),
      description: t('loanPrograms.rentalDesc'),
      features: t('loanFeatures.rental'),
    },
    {
      icon: Building,
      title: t('loanPrograms.commercialTitle'),
      description: t('loanPrograms.commercialDesc'),
      features: t('loanFeatures.commercial'),
    },
    {
      icon: RefreshCw,
      title: t('loanPrograms.refinanceTitle'),
      description: t('loanPrograms.refinanceDesc'),
      features: t('loanFeatures.refinance'),
    },
    {
      icon: Clock,
      title: t('loanPrograms.foreclosureTitle'),
      description: t('loanPrograms.foreclosureDesc'),
      features: t('loanFeatures.foreclosure'),
    },
    {
      icon: Scale,
      title: t('loanPrograms.evictionTitle'),
      description: t('loanPrograms.evictionDesc'),
      features: t('loanFeatures.eviction'),
    },
  ];

  const processSteps = [
    {
      step: 1,
      title: t('process.step1Title'),
      description: t('process.step1Desc'),
    },
    {
      step: 2,
      title: t('process.step2Title'),
      description: t('process.step2Desc'),
    },
    {
      step: 3,
      title: t('process.step3Title'),
      description: t('process.step3Desc'),
    },
    {
      step: 4,
      title: t('process.step4Title'),
      description: t('process.step4Desc'),
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-slate-900" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1636790920612-ed453c635e6c?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative container-main py-32 md:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="hero-content animate-fade-in-up">
              <p className="font-body text-amber-500 uppercase tracking-widest text-sm font-semibold mb-4">
                {t('hero.tagline')}
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {t('hero.title1')}<br />
                <span className="text-amber-500">{t('hero.title2')}</span> {t('hero.title3')}
              </h1>
              <p className="font-body text-slate-300 text-lg mb-8 max-w-xl">
                {t('hero.description')}
              </p>
              
              {/* Key Stats */}
              <div className="flex flex-wrap gap-8 mb-10">
                <div>
                  <p className="font-heading text-3xl font-bold text-amber-500">5-7</p>
                  <p className="font-body text-slate-400 text-sm">{t('hero.dayClosing')}</p>
                </div>
                <div>
                  <p className="font-heading text-3xl font-bold text-amber-500">75%</p>
                  <p className="font-body text-slate-400 text-sm">{t('hero.maxLtv')}</p>
                </div>
                <div>
                  <p className="font-heading text-3xl font-bold text-amber-500">$50M+</p>
                  <p className="font-body text-slate-400 text-sm">{t('hero.funded')}</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-gold rounded-none text-base" data-testid="hero-cta-btn">
                    {t('hero.getFundedToday')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:424-303-2130">
                  <Button className="btn-secondary rounded-none text-base bg-transparent border-white text-white hover:bg-white/10">
                    <Phone className="mr-2 w-5 h-5" />
                    424-303-2130
                  </Button>
                </a>
              </div>
            </div>

            {/* Quick Quote Form */}
            <div className="bg-white p-8 lg:p-10 animate-fade-in animation-delay-200" data-testid="quick-quote-form">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                {t('quote.title')}
              </h2>
              <p className="font-body text-slate-600 mb-6">
                {t('quote.subtitle')}
              </p>
              <LeadForm variant="compact" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="bg-slate-50 py-8 border-y border-slate-200" data-testid="trust-strip">
        <div className="container-main">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <p className="font-body text-slate-500 text-sm uppercase tracking-wider">{t('trustStrip.trusted')}</p>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-body text-slate-700 font-medium">{t('trustStrip.licensed')}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-body text-slate-700 font-medium">{t('trustStrip.directCapital')}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-body text-slate-700 font-medium">{t('trustStrip.fastDecisions')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="section-padding bg-white" data-testid="value-props-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="font-body text-amber-700 uppercase tracking-widest text-sm font-semibold mb-4">
              {t('valueProps.subtitle')}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('valueProps.title')}
            </h2>
            <p className="font-body text-slate-600 text-lg max-w-2xl mx-auto">
              {t('valueProps.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {valueProps.map((prop, index) => (
              <div 
                key={index}
                className="card-institutional text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`value-prop-${index}`}
              >
                <div className="value-icon mx-auto mb-6">
                  <prop.icon className="w-7 h-7" />
                </div>
                <p className="font-heading text-2xl font-bold text-amber-700 mb-2">
                  {prop.highlight}
                </p>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-3">
                  {prop.title}
                </h3>
                <p className="font-body text-slate-600 text-sm">
                  {prop.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Loan Programs Section */}
      <section className="section-padding bg-slate-50" data-testid="loan-programs-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="font-body text-amber-700 uppercase tracking-widest text-sm font-semibold mb-4">
              {t('loanPrograms.subtitle')}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('loanPrograms.title')}
            </h2>
            <p className="font-body text-slate-600 text-lg max-w-2xl mx-auto">
              {t('loanPrograms.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loanPrograms.map((program, index) => (
              <div 
                key={index}
                className="loan-card card-institutional animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`loan-program-${index}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-slate-900 flex items-center justify-center">
                    <program.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900">
                    {program.title}
                  </h3>
                </div>
                <p className="font-body text-slate-600 mb-6">
                  {program.description}
                </p>
                <ul className="space-y-2">
                  {program.features && program.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-amber-700" />
                      <span className="font-body text-sm text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/loans">
              <Button className="btn-secondary rounded-none" data-testid="view-all-loans-btn">
                {t('loanPrograms.viewAll')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section-padding bg-white" data-testid="how-it-works-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="font-body text-amber-700 uppercase tracking-widest text-sm font-semibold mb-4">
              {t('process.subtitle')}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('process.title')}
            </h2>
            <p className="font-body text-slate-600 text-lg max-w-2xl mx-auto">
              {t('process.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((item, index) => (
              <div 
                key={item.step}
                className="relative animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`process-step-${index}`}
              >
                {index < processSteps.length - 1 && (
                  <div className="timeline-connector" />
                )}
                <div className="step-number mb-6">
                  {item.step}
                </div>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-3">
                  {item.title}
                </h3>
                <p className="font-body text-slate-600 text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-20" data-testid="cta-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                {t('cta.title')}
              </h2>
              <p className="font-body text-slate-300 text-lg mb-8">
                {t('cta.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-gold rounded-none" data-testid="cta-get-funded-btn">
                    {t('cta.getFundedNow')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:424-303-2130">
                  <Button className="btn-secondary rounded-none bg-transparent border-white text-white hover:bg-white/10">
                    <Phone className="mr-2 w-5 h-5" />
                    {t('cta.call')} 424-303-2130
                  </Button>
                </a>
              </div>
            </div>
            <div className="bg-slate-800 p-8 border border-slate-700">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="font-heading text-3xl font-bold text-amber-500">5-7</p>
                  <p className="font-body text-slate-400 text-sm">{t('hero.dayClosing')}</p>
                </div>
                <div>
                  <p className="font-heading text-3xl font-bold text-amber-500">75%</p>
                  <p className="font-body text-slate-400 text-sm">{t('hero.maxLtv')}</p>
                </div>
                <div>
                  <p className="font-heading text-3xl font-bold text-amber-500">24hr</p>
                  <p className="font-body text-slate-400 text-sm">{t('cta.responseTime')}</p>
                </div>
                <div>
                  <p className="font-heading text-3xl font-bold text-amber-500">100+</p>
                  <p className="font-body text-slate-400 text-sm">{t('cta.loansFunded')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
